"use client";
import React, { useEffect, useRef, useState } from "react";
import styles from "./MultiVendorArchitecture.module.css";

const FEATURES = [
  {
    icon: "fa-solid fa-layer-group",
    title: "Modular Infrastructure",
    tagline: "Scalable Architecture That Grows with You",
    bullets: [
      "Microservice-based scaling (vendors, orders, payments)",
      "Node.js + Next.js + GraphQL APIs",
      "Dockerized + CI/CD ready",
      "Optimized caching & load balancing",
      "Auto-scaling cloud (AWS / GCP)",
    ],
  },
  {
    icon: "fa-solid fa-diagram-project",
    title: "Unified API Gateway",
    tagline: "One Core — Endless Integrations",
    bullets: [
      "REST & GraphQL APIs",
      "ERP/CRM connectors (Tally, HubSpot, Zoho)",
      "Webhook automation",
      "Real-time sync across platforms",
    ],
  },
  {
    icon: "fa-solid fa-robot",
    title: "Smart Commerce Engine",
    tagline: "Automation That Works While You Sleep",
    bullets: [
      "Dynamic pricing & commission logic",
      "Automated settlements",
      "Smart order routing (nearest vendor)",
      "Multi-region support",
    ],
  },
  {
    icon: "fa-solid fa-chart-line",
    title: "Intelligence & Insights",
    tagline: "AI-Powered Decisions, Not Just Data",
    bullets: [
      "AI-based sales forecasting",
      "Customer behavior clustering",
      "Fraud & anomaly detection",
      "Advanced reporting dashboards",
    ],
  },
];

const RAIL_LOGOS = [
  { icon: "fa-brands fa-shopify", name: "Shopify" },
  { icon: "fa-solid fa-indian-rupee-sign", name: "Razorpay" },
  { icon: "fa-brands fa-stripe", name: "Stripe" },
  { icon: "fa-solid fa-truck-fast", name: "Shiprocket" },
  { icon: "fa-brands fa-aws", name: "AWS" },
  { icon: "fa-brands fa-google", name: "Google Cloud" },
  { icon: "fa-brands fa-hubspot", name: "HubSpot" },
  { icon: "fa-solid fa-envelope", name: "Mailchimp" },
  { icon: "fa-brands fa-whatsapp", name: "WhatsApp Business" },
];

export default function MultiVendorArchitecture() {
  const gridRef = useRef(null);
  const diagRef = useRef(null);
  const railRef = useRef(null);
  const vendorsRef = useRef(null);
  const coreRef = useRef(null);
  const customersRef = useRef(null);
  const paymentsRef = useRef(null);
  const integrationsRef = useRef(null);

  const [lines, setLines] = useState([]);
  const pathsRef = useRef([]);
  const dotsRef = useRef([]);
  const animRef = useRef(null);
  const burstsRef = useRef([]); // active burst effects
  const burstsLayerRef = useRef(null);

  useEffect(() => {
    const root = gridRef.current;
    const diag = diagRef.current;
    const rail = railRef.current;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add(styles.inView)),
      { threshold: 0.15 }
    );
    if (root) root.querySelectorAll(`.${styles.card}`).forEach(el => obs.observe(el));
    if (diag) obs.observe(diag);
    if (rail) obs.observe(rail);

    function computeLines() {
      if (!diag || !coreRef.current) return;
      const d = diag.getBoundingClientRect();
      const getBox = (el) => {
        const r = el.getBoundingClientRect();
        return {
          cx: r.left + r.width / 2 - d.left,
          cy: r.top + r.height / 2 - d.top,
          top: r.top - d.top,
          bottom: r.bottom - d.top,
          left: r.left - d.left,
          right: r.right - d.left,
          w: r.width,
          h: r.height,
        };
      };

      const core = getBox(coreRef.current);
      const centerAttach = { x: core.cx, y: core.cy }; // end at exact center of API Core
      const vendors = vendorsRef.current ? getBox(vendorsRef.current) : null;
      const customers = customersRef.current ? getBox(customersRef.current) : null;
      const payments = paymentsRef.current ? getBox(paymentsRef.current) : null;
      const integrations = integrationsRef.current ? getBox(integrationsRef.current) : null;

      const out = [];
      if (vendors) {
        out.push({
          from: { x: vendors.cx, y: vendors.bottom - 2 },
          to: centerAttach,
          curve: 28, // smooth approach into center
        });
      }
      if (customers) {
        out.push({
          from: { x: customers.cx, y: customers.bottom - 2 },
          to: centerAttach,
          curve: 28,
        });
      }
      if (payments) {
        out.push({
          from: { x: payments.cx, y: payments.top + 2 },
          to: centerAttach,
          curve: -34, // upward to center
        });
      }
      if (integrations) {
        out.push({
          from: { x: integrations.cx, y: integrations.top + 2 },
          to: centerAttach,
          curve: -34,
        });
      }
      setLines(out);
    }

    computeLines();
    const ro = new ResizeObserver(computeLines);
    diag && ro.observe(diag);
    // observe node size changes too
    const roNodes = new ResizeObserver(computeLines);
    [vendorsRef.current, coreRef.current, customersRef.current, paymentsRef.current, integrationsRef.current]
      .forEach((el) => el && roNodes.observe(el));

    window.addEventListener("resize", computeLines);
    window.addEventListener("orientationchange", computeLines);
    if (document?.fonts?.ready) {
      document.fonts.ready.then(() => computeLines());
    }

    return () => {
      obs.disconnect();
      ro.disconnect();
      roNodes.disconnect();
      window.removeEventListener("resize", computeLines);
      window.removeEventListener("orientationchange", computeLines);
    };
  }, []);

  // Animate dots along paths
  useEffect(() => {
    cancelAnimationFrame(animRef.current);
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!lines.length) return;

    const metas = lines.map((_, i) => {
      const p = pathsRef.current[i];
      const len = p ? p.getTotalLength() : 0;
      return {
        len,
        dur: Math.max(2200, Math.min(5200, len * 6)), // duration based on length
        phase: (i * 300) % 2000, // slight offset per line
        prevNearEnd: false,
        lastBurst: 0,
      };
    });

    if (prefersReduced) {
      // Place dots at midpoints without animation
      metas.forEach((m, i) => {
        const p = pathsRef.current[i];
        const c = dotsRef.current[i];
        if (!p || !c || !m.len) return;
        const pt = p.getPointAtLength(m.len / 2);
        c.setAttribute('cx', pt.x);
        c.setAttribute('cy', pt.y);
      });
      return;
    }

    let start = performance.now();
    const tick = (now) => {
      const t = now - start;
      lines.forEach((_, i) => {
        const p = pathsRef.current[i];
        const c = dotsRef.current[i];
        const m = metas[i];
        if (!p || !c || !m.len || !m.dur) return;
        const prog = ((t + m.phase) % m.dur) / m.dur; // 0..1 looping
        const pt = p.getPointAtLength(prog * m.len);
        c.setAttribute('cx', pt.x);
        c.setAttribute('cy', pt.y);

        // detect arrival near end (API Core center)
        const nearEnd = prog > 0.985; // last 1.5% of the path
        if (nearEnd && !m.prevNearEnd && now - m.lastBurst > 400) {
          m.lastBurst = now;
          spawnBurst(pt.x, pt.y);
        }
        m.prevNearEnd = nearEnd;
      });

      // update active bursts
      updateBursts(now);
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [lines]);

  function spawnBurst(x, y) {
    const g = burstsLayerRef.current;
    if (!g) return;
    const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    ring.setAttribute('cx', x);
    ring.setAttribute('cy', y);
    ring.setAttribute('r', 0);
    ring.setAttribute('fill', 'none');
    ring.setAttribute('stroke', 'var(--cq-accent-2)');
    ring.setAttribute('stroke-width', '2');
    ring.setAttribute('filter', 'url(#glow)');

    const sparks = [];
    const sparkCount = 6;
    for (let i = 0; i < sparkCount; i++) {
      const s = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      s.setAttribute('r', '2.5');
      s.setAttribute('fill', 'var(--cq-accent-2)');
      s.setAttribute('filter', 'url(#glow)');
      g.appendChild(s);
      sparks.push({ el: s, angle: (Math.PI * 2 * i) / sparkCount + Math.random() * 0.4, dist: 34 + Math.random() * 18 });
    }

    g.appendChild(ring);
    const burst = { start: performance.now(), dur: 600, x, y, ring, sparks };
    burstsRef.current.push(burst);
  }

  function updateBursts(now) {
    const g = burstsLayerRef.current;
    if (!g) return;
    const keep = [];
    for (const b of burstsRef.current) {
      const p = Math.min(1, (now - b.start) / b.dur);
      const ease = p * (2 - p); // easeOutQuad
      const r = ease * 28;
      b.ring.setAttribute('r', String(r));
      b.ring.setAttribute('opacity', String(1 - p));
      // sparks
      for (const sp of b.sparks) {
        const dx = Math.cos(sp.angle) * sp.dist * ease;
        const dy = Math.sin(sp.angle) * sp.dist * ease;
        sp.el.setAttribute('cx', String(b.x + dx));
        sp.el.setAttribute('cy', String(b.y + dy));
        sp.el.setAttribute('opacity', String(1 - p));
      }
      if (p < 1) keep.push(b);
      else {
        // cleanup DOM
        g.removeChild(b.ring);
        b.sparks.forEach(sp => g.removeChild(sp.el));
      }
    }
    burstsRef.current = keep;
  }

  return (
    <section className={`${styles.section} py-5`}>
      <div className={styles.wrap}>
        <header className={styles.head}>
          <h3 className={styles.title}>
            Powering the Future of Multi-Vendor Commerce — Built for Scale, Speed & Smart Automation.
          </h3>
          <p className={styles.subtitle}>
            Every component of your marketplace — from vendor onboarding to payout reconciliation — runs on CodeQlik’s modular, API-driven framework engineered for 99.9% uptime and limitless growth.
          </p>
        </header>

        <div className={styles.split}>
          {/* Left: dynamic architecture graphic */}
          <div className={styles.left}>
            <div ref={diagRef} className={styles.diagram} aria-hidden>
              {/* dynamic SVG connectors */}
              <svg
                width="100%"
                height="100%"
                style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
                viewBox={`0 0 ${diagRef.current?.clientWidth || 600} ${diagRef.current?.clientHeight || 380}`}
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient id="g-accent" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor="rgba(245,131,39,0)" />
                    <stop offset="50%" stopColor="rgba(245,131,39,0.9)" />
                    <stop offset="100%" stopColor="rgba(245,131,39,0)" />
                  </linearGradient>
                  <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* bursts layer (rings + sparks) */}
                <g ref={burstsLayerRef}></g>
                {lines.map((ln, idx) => {
                  const mx = (ln.from.x + ln.to.x) / 2;
                  const my = (ln.from.y + ln.to.y) / 2 + ln.curve;
                  const d = `M ${ln.from.x},${ln.from.y} Q ${mx},${my} ${ln.to.x},${ln.to.y}`;
                  return (
                    <g key={idx}>
                      <path
                        ref={(el) => (pathsRef.current[idx] = el)}
                        d={d}
                        stroke="url(#g-accent)"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                        filter="url(#glow)"
                      />
                      <circle
                        ref={(el) => (dotsRef.current[idx] = el)}
                        r={4}
                        fill="var(--cq-accent-2)"
                        filter="url(#glow)"
                      />
                    </g>
                  );
                })}
              </svg>

              {/* nodes */}
              <div ref={vendorsRef} className={`${styles.node} ${styles.vendors}`}>
                <i className="fa-solid fa-people-group"></i> Vendors
              </div>
              <div ref={coreRef} className={`${styles.node} ${styles.core}`}>
                <i className="fa-solid fa-server"></i> API Core
              </div>
              <div ref={customersRef} className={`${styles.node} ${styles.customers}`}>
                <i className="fa-solid fa-user"></i> Customers
              </div>
              <div ref={paymentsRef} className={`${styles.node} ${styles.payments}`}>
                <i className="fa-solid fa-sack-dollar"></i> Payments
              </div>
              <div ref={integrationsRef} className={`${styles.node} ${styles.integrations}`}>
                <i className="fa-solid fa-plug"></i> Integrations
              </div>
            </div>
          </div>

          {/* Right: four feature cards */}
          <div ref={gridRef} className={styles.right}>
            <div className={styles.grid}>
              {FEATURES.map((f) => (
                <div key={f.title} className={styles.cardWrap}>
                  <article className={styles.card}>
                    <h5>
                      <i className={f.icon} style={{ color: "var(--cq-accent)" }}></i> {f.title}
                    </h5>
                    <p>{f.tagline}</p>
                    <ul className={styles.bullets}>
                      {f.bullets.map((b) => (
                        <li key={b}>
                          <i className="fa-solid fa-bolt"></i>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom marquee */}
        <div ref={railRef} className={styles.logoRail} aria-hidden>
          <div className={styles.logoTrack}>
            <div className={styles.railInner}>
              {RAIL_LOGOS.map((l, i) => (
                <span key={`${l.name}-${i}`} className={styles.logoChip}>
                  <i className={l.icon}></i> {l.name}
                </span>
              ))}
            </div>
            <div className={styles.railInner}>
              {RAIL_LOGOS.map((l, i) => (
                <span key={`dup-${l.name}-${i}`} className={styles.logoChip}>
                  <i className={l.icon}></i> {l.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-3">
          <em style={{ color: "var(--cq-muted)" }}>
            From local startups to global brands — CodeQlik builds multi-vendor systems engineered for intelligence, automation, and infinite scale.
          </em>
        </div>
      </div>
    </section>
  );
}
